import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { useDevModeStore } from '../stores/useDevModeStore'
import { useTelemetryStore, POI_DATA_MAP } from '../stores/useTelemetryStore'
import type { POIDataConfig } from '../stores/useTelemetryStore'
import { useCameraStore } from '../stores/useCameraStore'
import { useDashboardPanelStore } from '../stores/useDashboardPanelStore'
import { METRIC_CONFIGS, PANEL_DEFAULTS } from '../config/dashboardPanelDefaults'
import { dashboardPanelVertexShader, dashboardPanelFragmentShader } from '../shaders/dashboardPanel'
import { useDashboardPanelTexture } from '../hooks/useDashboardPanelTexture'

// Reusable vectors for flip logic (allocated once, reused per frame)
const _panelPos = new THREE.Vector3()
const _toCamera = new THREE.Vector3()
const _forward = new THREE.Vector3()

interface DashboardPanelProps {
  poiConfig: POIDataConfig
}

function DashboardPanel({ poiConfig }: DashboardPanelProps) {
  const groupRef = useRef<THREE.Group>(null)
  const materialRef = useRef<THREE.ShaderMaterial>(null)
  const currentRotYRef = useRef<number | null>(null)
  const animProgressRef = useRef(0)

  const pois = useCameraStore((s) => s.pois)

  // Read initial config from store once (for initial render values)
  const init = useRef(useDashboardPanelStore.getState().getEffectiveConfig(poiConfig.poiId)).current
  const baseSize = useRef(PANEL_DEFAULTS[poiConfig.poiId]?.baseSize ?? [3, 3]).current as [
    number,
    number,
  ]

  const poi = pois.find((p) => p.id === poiConfig.poiId)
  const metrics = METRIC_CONFIGS[poiConfig.poiId] ?? []
  const buildingName = poiConfig.label

  // Get initial summary for texture creation
  const initProgress = useRef(useDevModeStore.getState().dayNightProgress).current
  const initSummary = useRef(
    useTelemetryStore.getState().getSummaryAtProgress(poiConfig.poiId, initProgress)
  ).current

  const initHeading = init.heading || buildingName

  // Canvas texture (returns texture + imperative redraw function)
  const { texture, redraw } = useDashboardPanelTexture(
    init,
    initSummary,
    metrics,
    initHeading,
    baseSize[0]
  )

  // Shader uniforms (created once, updated per frame)
  const uniforms = useMemo(
    () => ({
      uTexture: { value: texture },
      uTime: { value: 0 },
      uOpacity: { value: 0 }, // Start at 0 — fade in when playing
      uGlowIntensity: { value: init.glowIntensity },
      uGlowColor: { value: new THREE.Color(init.accentColor) },
      uBorderOpacity: { value: 0.2 },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // Per-frame: read ALL dynamic data directly from stores (no React re-render dependency)
  useFrame((state) => {
    if (metrics.length === 0) return

    const cfg = useDashboardPanelStore.getState().getEffectiveConfig(poiConfig.poiId)
    const progress = useDevModeStore.getState().dayNightProgress
    const playing = useDevModeStore.getState().dayNightPlaying
    const sum = useTelemetryStore.getState().getSummaryAtProgress(poiConfig.poiId, progress)

    // "Ploop" slide-up animation based on day/night playing state
    const animTarget = playing ? 1 : 0
    animProgressRef.current = THREE.MathUtils.lerp(animProgressRef.current, animTarget, 0.08)
    if (Math.abs(animProgressRef.current - animTarget) < 0.001) animProgressRef.current = animTarget

    // Elastic overshoot easing for the pop-up feel
    const t = animProgressRef.current
    const eased = t < 1 ? 1 - Math.pow(1 - t, 3) * Math.cos(t * Math.PI * 0.5) : 1

    // Y offset: panels start below, slide up to final position
    const DROP_DISTANCE = 2
    const yOffset = (1 - eased) * -DROP_DISTANCE

    // Update shader uniforms
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = state.clock.elapsedTime
      // Opacity fades in alongside the slide (reaches full at ~60% of animation)
      materialRef.current.uniforms.uOpacity.value = Math.min(1, eased * 1.5) * cfg.panelOpacity
      materialRef.current.uniforms.uGlowIntensity.value = cfg.glowIntensity
      materialRef.current.uniforms.uGlowColor.value.set(cfg.accentColor)
      materialRef.current.uniforms.uTexture.value = texture
    }

    // Hide group when fully down (optimization)
    if (groupRef.current) {
      groupRef.current.visible = animProgressRef.current > 0.01
    }

    // Redraw canvas texture if data or config changed (key comparison inside redraw)
    const heading = cfg.heading || buildingName
    redraw(cfg, sum, metrics, heading)

    // Live position + scale (with ploop Y offset)
    if (groupRef.current && poi) {
      const poiTarget = poi.target
      groupRef.current.position.set(
        poiTarget.x + cfg.offset[0],
        poiTarget.y + cfg.offset[1] + yOffset,
        poiTarget.z + cfg.offset[2]
      )

      const mesh = groupRef.current.children[0] as THREE.Mesh | undefined
      if (mesh) {
        mesh.scale.set(
          cfg.scale * (cfg.panelWidth / baseSize[0]),
          cfg.scale * (cfg.panelHeight / baseSize[1]),
          1
        )
      }
    }

    // Flip logic
    if (groupRef.current && poi) {
      const baseRotY = cfg.rotationY

      groupRef.current.getWorldPosition(_panelPos)
      _toCamera.subVectors(state.camera.position, _panelPos)
      _forward.set(Math.sin(baseRotY), 0, Math.cos(baseRotY))

      const dot = _toCamera.dot(_forward)

      let targetRotY: number
      if (dot > 0.15) {
        targetRotY = baseRotY
      } else if (dot < -0.15) {
        targetRotY = baseRotY + Math.PI
      } else {
        targetRotY = currentRotYRef.current ?? baseRotY
      }

      if (currentRotYRef.current === null) {
        currentRotYRef.current = targetRotY
      }

      currentRotYRef.current = targetRotY
      groupRef.current.rotation.y = currentRotYRef.current
    }
  })

  // Read visibility from store (needed for render-gating -- useFrame can't run if we return null)
  const visible = useDashboardPanelStore((s) => s.overrides[poiConfig.poiId]?.visible ?? true)

  if (!poi || metrics.length === 0 || !visible) return null

  const target = poi.target

  return (
    <group
      ref={groupRef}
      position={[target.x + init.offset[0], target.y + init.offset[1], target.z + init.offset[2]]}
    >
      <mesh renderOrder={2}>
        <planeGeometry args={[baseSize[0], baseSize[1]]} />
        <shaderMaterial
          ref={materialRef}
          vertexShader={dashboardPanelVertexShader}
          fragmentShader={dashboardPanelFragmentShader}
          uniforms={uniforms}
          transparent
          side={THREE.DoubleSide}
          depthWrite={false}
        />
      </mesh>
    </group>
  )
}

export default function DashboardPanels() {
  const isLoaded = useTelemetryStore((s) => s.isLoaded)

  if (!isLoaded) return null

  return (
    <>
      {POI_DATA_MAP.map((poiConfig) => (
        <DashboardPanel key={poiConfig.poiId} poiConfig={poiConfig} />
      ))}
    </>
  )
}
